import { AxiosError } from 'axios';

/**
 * Ошибки входа и регистрации — отдельный модуль без алиасов, чтобы его брали тесты (`tests/auth-errors.test.ts`).
 */

export class AuthError extends Error {
  constructor(
    public code:
      | 'REQUIRED_FIELDS'
      | 'SHORT_PASSWORD'
      | 'INVALID_EMAIL'
      | 'INVALID_NAME'
      | 'INVALID_DATA'
      | 'INVALID_CREDENTIALS'
      | 'EMAIL_EXISTS'
      | 'RATE_LIMITED'
      | 'SERVER'
      | 'NETWORK',
  ) {
    super(code);
    this.name = 'AuthError';
  }
}

type InvalidField = 'email' | 'password' | 'name' | 'extras' | null;

/**
 * Какое поле отверг сервер (422 FastAPI: `detail[].loc` = ['body', '<поле>', …]). До 2026-10-27 любой 422 значил
 * «Email некорректный»: имя из одной буквы давало ложную ошибку почты, и человек уходил с формы.
 */
export function invalidField(err: unknown): InvalidField {
  if (!(err instanceof AxiosError) || err.response?.status !== 422) return null;
  const detail = (err.response.data as { detail?: unknown } | undefined)?.detail;
  if (!Array.isArray(detail)) return null;
  const fields = detail.flatMap((d) => (Array.isArray((d as { loc?: unknown })?.loc) ? [(d as { loc: unknown[] }).loc[1]] : []));
  if (fields.includes('email')) return 'email';
  if (fields.includes('password')) return 'password';
  if (fields.includes('name')) return 'name';
  if (fields.some((f) => f === 'attribution' || f === 'language_interface')) return 'extras';
  return null;
}

export function mapHttpError(err: unknown, fallback: AuthError['code']): AuthError {
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    if (status === 422) {
      const field = invalidField(err);
      if (field === 'email') return new AuthError('INVALID_EMAIL');
      if (field === 'password') return new AuthError('SHORT_PASSWORD');
      if (field === 'name') return new AuthError('INVALID_NAME');
      return new AuthError('INVALID_DATA');
    }
    if (status === 401 || status === 404) return new AuthError('INVALID_CREDENTIALS');
    if (status === 409) return new AuthError('EMAIL_EXISTS');
    // Раньше 429 и 500 при регистрации показывались как «Аккаунт с такой почтой уже есть» (fallback).
    if (status === 429) return new AuthError('RATE_LIMITED');
    if (status !== undefined && status >= 500) return new AuthError('SERVER');
    if (!err.response) return new AuthError('NETWORK');
  }
  return new AuthError(fallback);
}
